// klarnaPurchaseTracking.js
// The `purchase` event for Klarna orders.
//
// Every checkout fires `purchase` inline on the card path, right after
// /api/checkout-stripe comes back successful. Klarna can't do that: the page
// hands off to Klarna's site mid-payment and never runs again. The order is
// confirmed later, on KlarnaReturn.vue, which is a cold page load with none of
// the checkout's cart, plan or funnel numbering in memory.
//
// So the checkout stashes the params it already knows before the redirect, and
// the return page fires the event once the SERVER has confirmed the payment —
// which is the rule: `purchase` never fires on the payment page, never on a
// button click, only on confirmed success.
//
// `event_id` is minted at stash time, not at fire time. It is what the ad
// platforms dedupe on, so it has to survive a reload of the return page: a
// patient who refreshes must not be counted as two sales.
//
// The stash is keyed to the payment intent and read exactly once, so an
// abandoned attempt can never attach its revenue to a later order.

const KLARNA_PURCHASE_KEY = 'pending_klarna_purchase';

/**
 * Called by the checkout immediately before redirecting to Klarna.
 *
 * params — everything the card path puts on its own `purchase` MINUS the ids
 *          the server has not issued yet: funnel_step, event_value, currency,
 *          product_id, product_name, plan_id, quantity, coupon, form.
 */
export function stashKlarnaPurchase(paymentIntentId, params) {
    try {
        sessionStorage.setItem(
            KLARNA_PURCHASE_KEY,
            JSON.stringify({
                payment_intent_id: paymentIntentId,
                params: {
                    ...params,
                    currency: 'USD',
                    // Minted here so a refreshed return page reuses it instead of
                    // reporting a second sale.
                    event_id: crypto.randomUUID(),
                },
            })
        );
    } catch (e) {
        // sessionStorage unavailable — the order still completes, it just goes
        // untracked, exactly as it did before this existed.
    }
}

/**
 * Called by KlarnaReturn.vue AFTER /api/confirm-klarna-payment reports success.
 *
 * Single use: the stash is cleared before the event fires, so a double mount or
 * a fast refresh cannot report the same sale twice.
 *
 * ids — { transactionId, orderId } from the confirm response.
 */
export function firePendingKlarnaPurchase(paymentIntentId, ids) {
    let stash = null;
    try {
        stash = JSON.parse(sessionStorage.getItem(KLARNA_PURCHASE_KEY) || 'null');
        sessionStorage.removeItem(KLARNA_PURCHASE_KEY);
    } catch (e) {
        return;
    }

    if (!stash || stash.payment_intent_id !== paymentIntentId || !stash.params) return;
    if (!window.tracking) return;

    try {
        window.tracking.event('purchase', {
            ...stash.params,
            transaction_id: (ids && ids.transactionId) || undefined,
            order_id: (ids && ids.orderId) || (ids && ids.transactionId) || undefined,
        });
    } catch (error) {
        console.warn('[klarna] purchase event failed', error);
    }
}
